import { DocumentFormData } from "../Components/Documents/ViewDocs/Documents.types";
import { config } from "../Utility/Constants";
import { getWithAuth, postWithAuth, putWithAuth } from "./InitializeService";
import { getListItems } from "./ProfileService";

/**
 * Document Service (Spoke)
 * Domain-specific service methods for Documents module
 * All endpoints use PBS Enterprise API base URL: https://data-core.teampbs.com/enterprise/api
 */

/**
 * Field mapping for SharePoint column name compatibility
 * Maps form field names to actual SharePoint column names
 *
 * NOTE: The SharePoint middleware automatically handles "Id" suffix removal for lookup fields.
 * For example: Background_x0020_TypeId → Background_x0020_Type (server-side resolution)
 * Only add mappings here for special cases that require client-side transformation.
 */
const SHAREPOINT_FIELD_MAPPING: Record<string, string> = {
  // Currently empty - server-side handles lookup field "Id" suffix removal automatically
  // Add mappings here only for special transformation cases
};

/**
 * Fields that should be excluded from SharePoint metadata (not actual columns)
 * These fields are used for internal processing but don't exist as SharePoint columns
 */
const EXCLUDED_SHAREPOINT_FIELDS: string[] = [
  // Temporarily empty - EditDocumentPanel works without exclusions
  // 'MasterDocumentID', // This might be handled internally by the backend/stored differently
];

/**
 * Transform metadata field names and values for SharePoint compatibility
 * @param metadata - Original metadata from form
 * @returns Transformed metadata with correct SharePoint column names and values
 */
const transformMetadataForSharePoint = (
  metadata: DocumentFormData,
): DocumentFormData => {
  const transformed: DocumentFormData = {};

  Object.entries(metadata).forEach(([key, value]) => {
    // Skip excluded fields
    if (EXCLUDED_SHAREPOINT_FIELDS.includes(key)) {
      return;
    }

    // Apply field name mapping (if any)
    const sharePointFieldName = SHAREPOINT_FIELD_MAPPING[key] || key;

    // Pass through the value - server handles field type conversions
    transformed[sharePointFieldName] = value;
  });

  return transformed;
};

/**
 * Get staff documents for a user
 * @param userName - User login name in Windows NT format (e.g., domain\username)
 * @returns Promise with OData response containing Document array
 */
export const getDocuments = (userName: string) => {
  return getWithAuth("staffdocuments", {
    params: { userName },
  });
};

/**
 * Fetch a subsequent page of documents using an OData nextLink URL
 * @param nextLink - The full @odata.nextLink URL returned by a previous page response
 * @returns Promise with OData response for the next page
 */
export const getDocumentsByNextLink = (nextLink: string) => {
  // nextLink is a fully-qualified URL; axios respects it as-is over the baseURL
  return getWithAuth(nextLink);
};

/**
 * Get master document types filtered by role and region
 * @param role - Employee role (e.g., "Senior Analyst")
 * @param region - Employee region (e.g., "LA Metro")
 * @returns Promise with OData response containing DocumentType array
 */
export const getDocumentTypes = (role: string, region: string) => {
  // Role and region come straight from the staff record and can contain characters
  // that are significant in a query string - e.g. the "&" in "Travel & Events Manager"
  // silently split the query into an extra parameter. Passing them as params lets
  // axios encode them (Task 9813).
  return getWithAuth("masterdocumenttypes", {
    params: { role, region },
  });
};

/**
 * Get a user's approved regional transfers from the "User Regional Transfers" list
 * The "Expected Transfer Date is in the future" rule is applied client-side
 * (getQualifyingRegionalTransfer) so it compares against the user's local date.
 * @param fullName - User's full name (matched against the Name person field)
 * @returns Promise with list query response containing transfer items
 */
export const getUserRegionalTransfers = (fullName: string) => {
  return getListItems({
    siteUrl: "https://sharepoint.teampbs.com",
    listName: "User Regional Transfers",
    camlQuery: `<View><Query><Where><And><Eq><FieldRef Name='Name' LookupId='FALSE' /><Value Type='Text'>${fullName}</Value></Eq><Eq><FieldRef Name='Status' /><Value Type='Text'>Approved</Value></Eq></And></Where><OrderBy><FieldRef Name='Expected_x0020_Transfer_x0020_Da' Ascending='True' /></OrderBy></Query></View>`,
    rowLimit: 100,
  });
};

/**
 * Get document form template (form schema) for a document type
 * @param id - Document type ID (must be > 0)
 * @returns Promise with OData response containing FormTemplate with FormJSON
 */
export const getDocumentFormTemplate = (id: number) => {
  return getWithAuth(`documentformtemplates/${id}`);
};

/**
 * Get document metadata from SharePoint (full list item with all custom fields)
 * @param fileName - Document file name
 * @param siteUrl - SharePoint site URL
 * @param libraryName - Document library name
 * @param folderPath - Optional folder path
 * @returns Promise with document metadata response
 */
/**
 * Get document metadata from SharePoint list item
 * Uses PBS Enterprise API v2.0: GET /api/sharepoint/lists/items/{itemId}
 * @param listItemId - SharePoint list item ID (not file name)
 * @param siteUrl - SharePoint site URL
 * @param listName - SharePoint list name
 * @returns Promise with ListItemResult containing itemId, fieldValues, timestamps, etc.
 */
export const getDocumentMetadata = (
  listItemId: string,
  siteUrl: string,
  listName: string,
) => {
  const request = getWithAuth(
    `sharepoint/lists/items/${listItemId}?listName=${listName}&siteUrl=${siteUrl}`,
  );

  // Log the response
  request
    .then((response) => {
      // console.log("✅ getDocumentMetadata SUCCESS", {
      //   status: response.status,
      //   itemId: response.data?.itemId,
      //   hasFieldValues: !!response.data?.fieldValues,
      //   fieldValuesCount: response.data?.fieldValues
      //     ? Object.keys(response.data.fieldValues).length
      //     : 0,
      //   fieldValues: response.data?.fieldValues,
      //   isSuccess: response.data?.isSuccess,
      //   created: response.data?.created,
      //   modified: response.data?.modified,
      //   fullResponseKeys: response.data ? Object.keys(response.data) : [],
      // });
    })
    .catch((error) => {
      // console.log("❌ getDocumentMetadata ERROR", {
      //   error: error.message,
      //   status: error.response?.status,
      //   statusText: error.response?.statusText,
      //   errorData: error.response?.data,
      //   url: error.config?.url,
      //   params: error.config?.params,
      // });
    });

  return request;
};

/**
 * Upload document with metadata to SharePoint
 * @param file - File to upload
 * @param siteUrl - SharePoint site URL
 * @param libraryName - Document library name
 * @param folderPath - Subfolder path
 * @param metadata - Metadata fields as key-value pairs
 * @param overwrite - Whether to overwrite existing file (default: false)
 * @param user - User identifier for Author/Editor fields (email, DOMAIN\username, or SPID)
 * @param onUploadProgress - Optional callback for upload progress tracking
 * @returns Promise with upload response (camelCase properties)
 */
export const uploadDocument = (
  file: File,
  siteUrl: string,
  libraryName: string,
  folderPath: string,
  metadata: DocumentFormData,
  overwrite: boolean = false,
  user?: string,
  onUploadProgress?: (progressEvent: { loaded: number; total: number }) => void,
) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("siteUrl", siteUrl);
  formData.append("libraryName", libraryName);
  formData.append("folderPath", folderPath);
  formData.append("overwrite", overwrite.toString());

  // Add user context for proper Author/Editor attribution
  if (user) {
    formData.append("user", user);
  }

  // Transform metadata for SharePoint field compatibility
  const transformedMetadata = transformMetadataForSharePoint(metadata);

  // console.log("🚀 UPLOAD METADATA - Field mapping comparison", {
  //   operation: "UPLOAD",
  //   endpoint: "sharepoint/documents",
  //   originalMetadata: metadata,
  //   transformedMetadata: transformedMetadata,
  //   fieldsCount: Object.keys(transformedMetadata).length,
  //   transformationsApplied: Object.entries(metadata)
  //     .filter(([key]) => SHAREPOINT_FIELD_MAPPING[key])
  //     .map(([key]) => `${key} → ${SHAREPOINT_FIELD_MAPPING[key]}`),
  // });

  // Add all transformed metadata fields to formData
  // Skip null values and convert other types to strings
  Object.keys(transformedMetadata).forEach((key) => {
    const value = transformedMetadata[key];
    // Skip null values (FormData.append doesn't accept null)
    if (value === null || value === undefined) {
      return;
    }
    // Convert Date objects to ISO strings
    if (value instanceof Date) {
      formData.append(key, value.toISOString());
    }
    // Convert numbers and booleans to strings
    else if (typeof value === "number" || typeof value === "boolean") {
      formData.append(key, String(value));
    }
    // Strings can be appended directly
    else if (typeof value === "string") {
      formData.append(key, value);
    }
  });

  return postWithAuth(`sharepoint/documents`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
    onUploadProgress: (progressEvent: any) => {
      if (onUploadProgress && progressEvent.total) {
        onUploadProgress({
          loaded: progressEvent.loaded,
          total: progressEvent.total,
        });
      }
    },
  });
};

/**
 * Update document metadata in SharePoint
 * @param fileName - Name of the file to update
 * @param siteUrl - SharePoint site URL
 * @param libraryName - Document library name
 * @param folderPath - Subfolder path
 * @param metadata - Metadata fields to update
 * @returns Promise with update response (camelCase properties)
 */
export const updateDocumentMetadata = (
  fileName: string,
  siteUrl: string,
  libraryName: string,
  folderPath: string,
  metadata: DocumentFormData,
) => {
  // Transform metadata for SharePoint field compatibility
  const transformedMetadata = transformMetadataForSharePoint(metadata);

  return putWithAuth(`sharepoint/documents/${fileName}`, {
    siteUrl,
    libraryName,
    folderPath,
    metadata: transformedMetadata,
  });
};

/**
 * Delete document from SharePoint using v2.0 API
 * @param fileName - Name of the file to delete
 * @param siteUrl - SharePoint site URL
 * @param libraryName - Document library name
 * @param folderPath - Subfolder path (optional)
 * @returns Promise with 204 No Content response
 */
export const deleteDocument = (
  fileName: string,
  siteUrl: string,
  libraryName: string,
  folderPath?: string,
) => {
  // v2.0 API uses POST with body parameters instead of DELETE with query params
  return postWithAuth(`sharepoint/documents/delete`, {
    fileName,
    siteUrl,
    libraryName,
    ...(folderPath && { folderPath }),
  });
};

/**
 * Get setup list options from SharePoint
 * Maps optionsList field values to SharePoint list names
 * @param listName - Setup list name or field identifier (e.g., 'Background Types', 'backgroundTypeLevelTwoBackground')
 * @param siteUrl - SharePoint site URL (defaults to config.url.SHAREPOINT_SITE_URL if not provided)
 * @returns Promise with array of OptionItem { key, text }
 */
export const getSetupListOptions = async (
  listName: string,
  siteUrl: string = config.url.SHAREPOINT_SITE_URL,
): Promise<{ key: string; text: string }[]> => {
  // Validate input
  if (
    !listName ||
    typeof listName !== "string" ||
    listName.trim().length === 0
  ) {
    console.warn(`Invalid setup list name: ${listName}`);
    return [];
  }

  const trimmedListName = listName.trim();

  // Map field identifiers to SharePoint list names
  // These are the values that come from FormJSON optionsList field
  const fieldIdentifierMapping: Record<string, string> = {
    backgroundTypeLevelTwoBackground: "Background Types",
    trainingTypeRestraintTraining: "Crisis Training Types",
    documentTypeCredentialingForms: "Credentialing Form Types",
    documentTypeRequiredTraining:
      "Required Training Acknowledgement and Agreement Document Types",
    trainingTypeCredentialingRequired: "Credentialing Required Training Types",
    certificationTypeCredentialingCertification:
      "Credentialing Certification Types",
    documentTypeMotorVehicle: "MVR Types",
    documentTypeCovid19: "Covid-19 Vaccination Document Types",
  };

  // Map SharePoint list names (for direct list name usage)
  const listNameMapping: Record<string, string> = {
    "Background Types": "Background Types",
    "Crisis Training Types": "Crisis Training Types",
    "Credentialing Form Types": "Credentialing Form Types",
    "Required Training Types": "Required Training Types",
    "Required Training Acknowledgement and Agreement Document Types":
      "Required Training Acknowledgement and Agreement Document Types",
    "Credentialing Required Training Types":
      "Credentialing Required Training Types",
    "Credentialing Certification Types": "Credentialing Certification Types",
    "MVR Types": "MVR Types",
    "Covid-19 Vaccination Types": "Covid-19 Vaccination Types",
    "Covid-19 Vaccination Document Types":
      "Covid-19 Vaccination Document Types",
  };

  // First check if it's a field identifier, then check if it's a direct list name
  let sharePointListName =
    fieldIdentifierMapping[trimmedListName] || listNameMapping[trimmedListName];

  // If not found in either mapping, use the trimmed name directly (might be a valid list name)
  if (!sharePointListName) {
    // Check if it looks like a field identifier (camelCase) vs a list name (Title Case with spaces)
    // If it's camelCase and not in our mapping, warn about it
    const isCamelCase = /^[a-z][a-zA-Z0-9]*$/.test(trimmedListName);
    if (isCamelCase) {
      console.warn(`Unknown setup list field identifier: ${trimmedListName}`);
      return [];
    }
    // Otherwise, assume it's a valid SharePoint list name and use it directly
    sharePointListName = trimmedListName;
  }

  try {
    // Call PBS Enterprise API SharePoint list endpoint
    // Using POST /api/sharepoint/lists/query endpoint (as per Postman collection)
    // This endpoint supports viewFields for selecting specific fields and avoids URL length limits
    const response = await postWithAuth(`sharepoint/lists/query`, {
      siteUrl: siteUrl,
      listName: sharePointListName,
      viewFields: ["ID", "Title"],
      rowLimit: 500,
      showSystemFields: false,
    });

    // Transform SharePoint list items to OptionItem format
    // Response format from /api/sharepoint/lists/query can be:
    // - Direct array: [{ itemId, fieldValues: { ID, Title }, ... }]
    // - Object with items: { items: [...], totalCount: number }
    const responseData = response.data;
    const items = Array.isArray(responseData)
      ? responseData
      : responseData?.items || responseData?.value || [];

    return items
      .map((item: any) => {
        // Handle different response formats
        // Format from /api/sharepoint/lists/query: { itemId: number, fieldValues: { ID: number, Title: string } }
        const itemId =
          item.itemId || item.fieldValues?.ID || item.ID || item.Id;
        const itemTitle = item.fieldValues?.Title || item.Title || item.title;

        return {
          key: itemId?.toString() || "",
          text: itemTitle || "",
        };
      })
      .filter((item: { key: string; text: string }) => item.key && item.text) // Filter out invalid items
      .sort((a: any, b: any) => {
        // Sort alphabetically (case-sensitive: A-Z then a-z)
        // Empty/None values are already filtered out above
        return a.text.localeCompare(b.text);
      });
  } catch (error) {
    // Improved error message showing the actual SharePoint list name instead of field identifier
    const errorMessage =
      trimmedListName !== sharePointListName
        ? `Failed to load setup list "${sharePointListName}" (from field identifier "${trimmedListName}")`
        : `Failed to load setup list "${sharePointListName}"`;
    console.error(errorMessage, error);
    return [];
  }
};

/**
 * Save a signed eSign document
 * @param payload - Signature submission payload
 * @returns Promise with save response
 */
export const saveESignDocument = (payload: {
  signatureImage: string;
  documentID: number;
  userLoginName: string;
  userName: string;
  region: string;
  role: string;
  suggestionID: number;
  ruleID: number;
}) => {
  return postWithAuth(`mysite/esign/save`, payload);
};

/**
 * Get pages for an eSign document
 * @param documentId - The eSign document ID
 * @returns Promise with pages array
 */
export const getESignDocumentPages = (documentId: number) => {
  return getWithAuth(`mysite/esign/${documentId}/pages`);
};

/**
 *
 * @param suggestionId
 * @param dismissedNotes
 * @returns
 */

export const dismissSuggestion = (
  suggestionId: number,
  dismissedNotes: string = "",
) => {
  return putWithAuth(`mysite/suggestions/${suggestionId}/dismiss`, {
    dismissedNotes,
    validateBeforeDismiss: false,
  });
};

/**
 * Get eSign documents for a user
 * @param loginName - User login name without domain prefix (e.g., "npanic")
 * @param roleId - User role ID
 * @param regionId - User region ID
 * @returns Promise with eSign documents array
 */
export const getESignDocuments = (
  loginName: string,
  roleId: number,
  regionId: number,
) => {
  return getWithAuth(
    `mysite/esign?loginName=${loginName}&roleId=${roleId}&regionId=${regionId}`,
  );
};

/**
 * Call a generic API endpoint for field validation
 * Used for custom validation endpoints specified in FormJSON field.validateAPI
 * @param url - Relative API URL (e.g., 'validation/checkDuplicate?value=...')
 * @returns Promise with validation response { Valid: boolean, Message: string }
 */
export const callValidationAPI = async (
  url: string,
): Promise<{ Valid: boolean; Message: string }> => {
  try {
    const response = await getWithAuth(url);
    return response.data;
  } catch (error) {
    console.error(`Validation API call failed: ${url}`, error);
    return {
      Valid: false,
      Message: "Validation failed due to server error",
    };
  }
};

/**
 * Get user information by ID
 * Used for expanding PeoplePicker field values to display names
 * @param userId - User ID, login name (domain\username), or email address
 * @returns Promise with user information { id: string, displayName: string, email: string }
 *
 * API Endpoint: GET /api/users?loginname=... or GET /api/users?email=...
 * Reference: PBS Enterprise APIs - Users.postman_collection.json
 * Response: Single UserProfileDto object (not OData array)
 */
export const getUserById = async (
  userId: string,
): Promise<{ id: string; displayName: string; email: string }> => {
  try {
    // PBS Enterprise API Users endpoint with conditional routing:
    // - If userId contains '@' → try email lookup
    // - Otherwise → try loginname lookup (domain\username format)
    // Use $select for performance (only fetch needed fields)

    let response;
    const isEmail = userId.includes("@");

    try {
      if (isEmail) {
        // Email lookup: GET /api/users?email=user@domain.com&$select=ID,FullName,Email
        response = await getWithAuth(
          `users?${userId}&$select=ID,FullName,Email,LoginName`,
        );
      } else {
        // Login name lookup: GET /api/users?loginname=DOMAIN\username&$select=ID,FullName,Email
        response = await getWithAuth(
          `users?loginname=${userId}&$select=ID,FullName,Email,LoginName`,
        );
      }
    } catch (firstAttemptError) {
      // If first attempt fails, try the other lookup method
      if (isEmail) {
        // Email failed, try login name
        response = await getWithAuth(
          `users?loginname=${userId}&$select=ID,FullName,Email,LoginName`,
        );
      } else {
        // Login name failed, try email
        response = await getWithAuth(
          `users?email=${userId}&$select=ID,FullName,Email,LoginName`,
        );
      }
    }

    // Response is single UserProfileDto object (not OData array)
    const userData = response.data;
    return {
      id: userData.id || userData.ID || userId,
      displayName: userData.fullName || userData.FullName || `User ${userId}`,
      email: userData.email || userData.Email || "",
    };
  } catch (error) {
    console.error(`Failed to load user ${userId}:`, error);
    // Return fallback data with the original userId
    return {
      id: userId,
      displayName: `User ${userId}`,
      email: "",
    };
  }
};

/**
 * Get items from the SharePoint Site Collection Recycle Bin
 * @param siteUrl - SharePoint site URL
 * @returns Promise with array of RecycleBinItem
 */
export const getRecycleBinItems = (siteUrl: string) => {
  return postWithAuth(`sharepoint/documents/recyclebin`, { siteUrl });
};

/**
 * Restores an item from the SharePoint Site Collection Recycle Bin
 * @param itemId - Unique GUID of the recycled item
 * @param siteUrl - SharePoint site URL
 * @returns Promise with 204 No Content response
 */
export const restoreRecycleBinItem = (itemId: string, siteUrl: string) => {
  return postWithAuth(`sharepoint/documents/recyclebin/restore`, {
    siteUrl,
    itemId,
  });
};
